<template>
  <div>
    <ContentHeader />

    <!-- Loading -->
    <div v-if="loading" class="loading">
      <div class="loader"></div>
      <img src="@/assets/img/logo.jpg" alt="logo" width="496" height="99" class="loaderLogo">
    </div>

    <!-- Only show section if there is at least one project -->
    <section v-if="!loading && projects.length > 0" class="reference-section py-5 bg-light">
      <div class="container">
        <div class="text-center mb-4">
          <h1 class="leadTitle">Project References</h1>
          <p class="text-muted">Our successful elevator and lift projects delivered for trusted partners</p>
        </div>

        <!-- Search -->
        <div class="row justify-content-center mb-4">
          <div class="col-md-6">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control shadow-sm"
              placeholder="Search projects..."
            />
          </div>
        </div>

        <!-- Projects Grid -->
        <div class="row g-4">
          <div
            class="col-md-4 col-sm-6"
            v-for="project in paginatedProjects"
            :key="project.id"
          >
            <div class="card border-0 shadow-sm h-100 project-card">
              <!-- Only show image if it exists -->
              <img
                v-if="project.image"
                :src="fullImageUrl(project.image)"
                :alt="project.name"
                class="card-img-top"
              />
              <div class="card-body">
                <h5 class="card-title fw-bold">{{ project.name }}</h5>
                <p class="card-text text-muted">{{ project.description }}</p>
                <p class="text-primary fw-semibold">{{ project.client }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="text-center mt-4">
          <button
            class="btn btn-outline-primary me-2"
            :disabled="currentPage === 1"
            @click="prevPage"
          >
            &lt;
          </button>

          <span>{{ currentPage }} / {{ totalPages }}</span>

          <button
            class="btn btn-outline-primary ms-2"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            &gt;
          </button>
        </div>
      </div>
    </section>

    <!-- Optional: Show "No projects found" message -->
    <section v-else-if="!loading && projects.length === 0" class="py-5 text-center">
      <div class="container">
        <h3>No projects found.</h3>
        <p>Please check back later or contact our team for references.</p>
      </div>
    </section>

    <ContentFooter />
  </div>
</template>

<script>
import ContentHeader from "@/components/ContentHeader.vue";
import ContentFooter from "@/components/ContentFooter.vue";
import axios from "axios";

export default {
  name: "ProjectReference",
  components: { ContentHeader, ContentFooter },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 6,
      projects: [],
      apiBase: process.env.VUE_APP_API_BASE,
      apiToken: process.env.VUE_APP_API_TOKEN,
      loading: true,
    };
  },
  computed: {
    filteredProjects() {
      const query = this.searchQuery.toLowerCase();
      return this.projects.filter(
        (project) =>
          project.name.toLowerCase().includes(query) ||
          project.description.toLowerCase().includes(query) ||
          project.client.toLowerCase().includes(query)
      );
    },
    totalPages() {
      return Math.ceil(this.filteredProjects.length / this.itemsPerPage);
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProjects.slice(start, start + this.itemsPerPage);
    },
  },
  methods: {
    async fetchProjects() {
      try {
        const res = await axios.get(`${this.apiBase}/api/projects`, {
          headers: { Authorization: `Bearer ${this.apiToken}` },
        });
        this.projects = res.data || [];
      } catch (err) {
        console.error("API Error:", err);
        this.projects = [];
      } finally {
        this.loading = false;
      }
    },
	fullImageUrl(path) {
	if (!path) return null; // No placeholder, skip if empty
	if (path.startsWith("http")) return path;
	
	// Ensure slash exists between domain and path
	const base = this.apiBase.endsWith('/') ? this.apiBase.slice(0, -1) : this.apiBase;
	const cleanPath = path.startsWith('/') ? path : '/' + path;

	return `${base}/storage${cleanPath}`;
	},
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchProjects();
  },
};
</script>

<style scoped>
.referenceBanner {
  background-color: #0d6efd;
  color: #fff;
  text-align: center;
  padding: 60px 0 40px;
}

.project-card img.card-img-top {
  height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.project-card:hover img.card-img-top {
  transform: scale(1.05);
}

.project-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.project-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}
</style>
